import { useEffect, useRef } from 'react';

const metrics = [
  {
    value: '+32%',
    label: 'Increase in booked appointments',
    sector: 'Healthcare Practice',
    description: 'Automated intake and scheduling recovery system deployed for a multi-location clinical group.',
  },
  {
    value: '–45%',
    label: 'Reduction in missed calls',
    sector: 'Lending Advisory',
    description: 'Signal capture and automated follow-up architecture eliminated lead leakage across inbound channels.',
  },
  {
    value: '+60%',
    label: 'Improvement in response time',
    sector: 'Construction Firm',
    description: 'Field-to-office communication system reduced project coordination latency and approval bottlenecks.',
  },
];

interface CTASectionProps {
  onConsultationClick: () => void;
}

export default function CTASection({ onConsultationClick }: CTASectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('[data-animate]');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Metrics section */}
      <section className="bg-[#FBFBFD] dark:bg-[#1A1A1D] py-24 md:py-32 border-t border-[#E8E8ED] dark:border-[#252528]">
        <div className="max-w-7xl mx-auto px-6 md:px-12" ref={sectionRef}>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#5BBFB5]" />
              <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
                Measured Outcomes
              </span>
              <span className="w-8 h-px bg-[#5BBFB5]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] tracking-tight mb-4">
              Results Defined by<br />
              <span className="text-[#C9A840]">Operational Precision</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {metrics.map((m, i) => (
              <div
                key={m.value}
                data-animate
                style={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  transition: `opacity 0.7s ease ${i * 0.12}s, transform 0.7s ease ${i * 0.12}s`,
                }}
                className="group bg-white dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] p-8 md:p-10 hover:border-[#C9A840]/40 dark:hover:border-[#C9A840]/30 transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-[2px] bg-[#C9A840] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                <span className="block text-5xl md:text-6xl font-black text-[#C9A840] mb-4 tracking-tight">
                  {m.value}
                </span>
                <span className="block text-[#1D1D1F] dark:text-[#F2F0EC] text-base font-semibold mb-2">
                  {m.label}
                </span>
                <span className="block text-[#A08840] text-xs font-medium mb-5" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  {m.sector}
                </span>
                <div className="w-8 h-px bg-[#E8E8ED] dark:bg-[#252528] mb-5" />
                <p className="text-[#6E6E73] dark:text-[#96969E] text-sm leading-relaxed">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority strip */}
      <section className="bg-white dark:bg-[#0B0B0C] py-10 border-t border-[#E8E8ED] dark:border-[#252528]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-[#6E6E73] dark:text-[#96969E] text-xs text-center md:text-left" style={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Built for high-stakes industries where precision is required
            </p>
            <div className="hidden md:flex items-center gap-10">
              {['Healthcare', 'Construction', 'Finance', 'Lending'].map((item, i) => (
                <span key={item} className="flex items-center gap-2 text-[#D2D2D7] dark:text-[#3A3A3E] text-xs" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  {i > 0 && <span className="w-px h-3 bg-[#D2D2D7] dark:bg-[#3A3A3E]" />}
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — full visual */}
      <section id="cta" className="relative py-28 md:py-40 overflow-hidden bg-[#0B0B0C]">
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("https://public.readdy.ai/ai/img_res/34343be01de1561c957748bb4f9f6b76.jpg")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-[#0B0B0C]/60 to-[#0B0B0C]/90" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(201,168,64,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,64,0.03) 1px, transparent 1px)`,
            backgroundSize: '72px 72px',
          }}
        />

        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l border-t border-[#C9A840]/20 pointer-events-none" />
        <div className="absolute top-0 right-0 w-32 h-32 border-r border-t border-[#C9A840]/20 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 border-l border-b border-[#C9A840]/20 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r border-b border-[#C9A840]/20 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center" ref={ctaRef}>
          <div className="inline-flex items-center gap-3 mb-10">
            <span className="w-10 h-px bg-[#5BBFB5]" />
            <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.25em', textTransform: 'uppercase' }}>
              Get Started
            </span>
            <span className="w-10 h-px bg-[#5BBFB5]" />
          </div>

          <h2
            data-animate
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
            className="text-4xl md:text-6xl font-bold text-[#F2F0EC] leading-tight tracking-tight mb-6"
          >
            Install Operational Intelligence<br />
            <span className="text-[#C9A840]">in Your Business.</span>
          </h2>

          <div className="w-16 h-px bg-[#C9A840]/50 mx-auto mb-8" />

          <p
            data-animate
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s' }}
            className="text-[#96969E] text-xl leading-relaxed max-w-xl mx-auto mb-14"
          >
            Engage Auditore AI to build the operational infrastructure your
            organization needs to execute at a higher level — without disruption.
          </p>

          <button
            data-animate
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}
            onClick={onConsultationClick}
            className="whitespace-nowrap inline-block px-16 py-5 bg-[#C9A840] text-[#0B0B0C] text-sm font-bold hover:bg-[#A08840] transition-all duration-300 cursor-pointer"
            style={{ letterSpacing: '0.18em', textTransform: 'uppercase' } as React.CSSProperties}
          >
            Schedule Consultation
          </button>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-8 text-[#4A4A4A] text-xs" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {['No long-term contract required', 'Response within one business day', 'Fully customized engagement'].map((item) => (
              <span key={item} className="flex items-center gap-2">
                <span className="w-1 h-1 bg-[#C9A840]/40 rounded-full" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
